import { IonButton, IonContent, IonHeader, IonPage, IonTitle, IonToolbar } from "@ionic/react";
import { usePanelFlow } from "components/PanelFlow";
import { IssueKeyType, IssueType } from "model";
import { createRequestASubPath } from "routes/routeData";
import { Colors, useFeatureFlags } from "swing-components";

import { translate } from "~utils";

import { ReportIssueKey, ReportIssueModalProps } from "../types";

type ReportIssueConfirmProps = ReportIssueModalProps & {
  goToPanel: (panelKey: string) => void;
};

export function ReportIssueConfirm(props: ReportIssueConfirmProps) {
  const { goToPanel, onDismiss, state, extraProps } = props;

  // Feature flag for improved report issue flow
  const { flags } = useFeatureFlags();
  const isImprovedReportIssueEnabled = Boolean(flags?.reportAnIssueImproved);

  const submissionMessage = getIssueOutput(
    state.reportedIssues,
    onDismiss,
    isImprovedReportIssueEnabled,
    extraProps.subName,
  );

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{translate("REPORT_ISSUE_CONFIRM_HEADER")}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div css={{ marginTop: 24, padding: 16, background: Colors.white200 }}>
          {submissionMessage}
          <div
            css={{
              display: "flex",
              flexDirection: "column",
              gap: 16,
              marginTop: 24,
            }}
          >
            <IonButton onClick={onDismiss}>
              {translate("REPORT_ISSUE_SECONDARY_BUTTON_TEXT")}
            </IonButton>
            <IonButton
              onClick={() => goToPanel(ReportIssueKey.ReportIssueFormPanel)}
              fill="outline"
            >
              {translate("REPORT_ISSUE_PRIMARY_BUTTON_TEXT")}
            </IonButton>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}

export function ReportIssueConfirmPanel() {
  const panelFlowProps = usePanelFlow();
  const { goToPanel, state, setState, extraProps } = panelFlowProps;

  return (
    <ReportIssueConfirm
      goToPanel={goToPanel}
      state={state}
      setState={setState}
      extraProps={extraProps}
      onDismiss={extraProps.onDismiss}
    />
  );
}

/********* Helper Functions ********/
export function getIssueOutput(
  issueType: IssueKeyType[],
  onDismiss: () => void,
  isImprovedReportIssueEnabled: boolean,
  subName?: string,
) {
  const showDefaultMessage =
    issueType.includes(IssueType.Late) ||
    issueType.includes(IssueType.LunchLate) ||
    issueType.includes(IssueType.UrgentConcern) ||
    (!isImprovedReportIssueEnabled && issueType.includes(IssueType.SubLeftEarly));

  const urgentHelpLine = issueType.includes(IssueType.UrgentConcern) && (
    <p
      css={{
        marginTop: 16,
        a: {
          fontWeight: 600,
          textDecoration: "none",
        },
      }}
    >
      <span
        dangerouslySetInnerHTML={{
          __html: translate("REPORT_ISSUE_CONFIRM_IMMEDIATE_ASSISTANCE"),
        }}
      />
      .
    </p>
  );

  // No Show - Approved (gave advance notice) - NEW BEHAVIOR ONLY
  if (isImprovedReportIssueEnabled && issueType.includes(IssueType.NoShowApproved)) {
    return (
      <>
        <div
          css={{ margin: 0 }}
          dangerouslySetInnerHTML={{
            __html: translate("REPORT_ISSUE_CONFIRM_NO_SHOW_APPROVED_MESSAGE"),
          }}
          onClick={(e) => {
            if ((e.target as HTMLElement).tagName === "A") {
              onDismiss();
            }
          }}
        />
        {urgentHelpLine}
      </>
    );
  }

  // No Show - Confirmed
  if (issueType.includes(IssueType.NoShowConfirmed)) {
    if (isImprovedReportIssueEnabled) {
      // New behavior: simplified message
      return (
        <>
          <p css={{ margin: 0 }}>{translate("REPORT_ISSUE_CONFIRM_NO_SHOW_CONFIRMED_MESSAGE")}</p>
          {urgentHelpLine}
        </>
      );
    } else {
      // Old behavior: message with link to request a sub
      return (
        <>
          <p css={{ margin: 0 }}>
            {translate("REPORT_ISSUE_CONFIRM_NO_SHOW_CONFIRMED_MESSAGE_OLD")},{" "}
            {
              // For some bizarre reason, IonRouter doesn't work in IonModals.
              // I am using an a href here, but any fix is welcome.
            }
            <a href={`/#${createRequestASubPath()}`} onClick={() => onDismiss()}>
              {translate("REPORT_ISSUE_CONFIRM_NO_SHOW_CONFIRMED_MESSAGE_LINK_TEXT")}.
            </a>
          </p>
          {urgentHelpLine}
        </>
      );
    }
  }

  // No Show - Unconfirmed
  if (issueType.includes(IssueType.NoShowUnConfirmed)) {
    if (isImprovedReportIssueEnabled) {
      // New behavior: HTML message
      return (
        <>
          <div
            css={{ margin: 0 }}
            dangerouslySetInnerHTML={{
              __html: translate("REPORT_ISSUE_CONFIRM_NO_SHOW_UNCONFIRMED_MESSAGE"),
            }}
          />
          {urgentHelpLine}
        </>
      );
    } else {
      // Old behavior: plain text message with subName
      return (
        <>
          <p css={{ margin: 0 }}>
            {translate(
              "REPORT_ISSUE_CONFIRM_NO_SHOW_UNCONFIRMED_MESSAGE_OLD",
              subName || "the sub",
            )}
          </p>
          {urgentHelpLine}
        </>
      );
    }
  }

  // Left Early - Approved (released early) - NEW BEHAVIOR ONLY
  if (isImprovedReportIssueEnabled && issueType.includes(IssueType.SubLeftEarlyApproved)) {
    return (
      <>
        <p css={{ margin: 0 }}>{translate("REPORT_ISSUE_CONFIRM_LEFT_EARLY_APPROVED_MESSAGE")}</p>
        {urgentHelpLine}
      </>
    );
  }

  // Left Early - Unapproved (left without permission) - NEW BEHAVIOR ONLY
  if (isImprovedReportIssueEnabled && issueType.includes(IssueType.SubLeftEarly)) {
    return (
      <>
        <p css={{ margin: 0 }}>{translate("REPORT_ISSUE_CONFIRM_LEFT_EARLY_UNAPPROVED_MESSAGE")}</p>
        {urgentHelpLine}
      </>
    );
  }

  if (issueType.length === 0 || showDefaultMessage) {
    // Deleting an issue or issue type with default message.
    return (
      <>
        <p css={{ margin: 0 }}>{translate("REPORT_ISSUE_CONFIRM_DEFAULT_MESSAGE")}.</p>
        {urgentHelpLine}
      </>
    );
  }

  throw new Error("issueType not handled");
}
